import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    LayoutDashboard,
    User,
    CalendarDays,
    Utensils,
    PhoneCall,
    CreditCard,
    Receipt,
    CircleHelp,
    Bell,
    Search,
    ChevronDown,
    ChevronRight,
    Info,
    Activity,
    Heart,
    Apple,
    Target,
    Menu,
    X,
    Crown
} from "lucide-react";
import { Pencil } from 'lucide-react';
import { Clock } from 'lucide-react';
import { Video } from 'lucide-react';
import { Coffee, Salad, UtensilsCrossed } from 'lucide-react';
import { Phone } from 'lucide-react';
import { Paperclip } from 'lucide-react';
import { getUserData, getAuthData, isLoggedIn } from "../../api/storageService";

import PersonalInfo from "./PersonalInfo";
import MedicalHistory from "./MedicalHistory";
import Lifestyle from "./Lifestyle";
import DietaryPreferences from "./DietaryPreferences";
import HealthGoals from "./HealthGoals";
import MyAppointments from "./MyAppointments";
import DietPlans from "./DietPlans";
import FollowupSessions from "./FollowupSessions";
import PurchasesPlans from "./PurchasesPlans";
import PaymentHistory from "./PaymentHistory";
import SupportEnquiry from "./SupportEnquiry";
const DashboardOverview = () => {
    const navigate = useNavigate();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [activeComponent, setActiveComponent] = useState("dashboard");
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [userData, setUserData] = useState(null);
    const menuItems = [
        { id: "dashboard", icon: LayoutDashboard, label: "Dashboard Overview", active: true },
        { id: "profile", icon: User, label: "Manage Profile", hasDropdown: true },
        { id: "appointments", icon: CalendarDays, label: "My Appointments" },
        { id: "dietplans", icon: Utensils, label: "Diet Plans" },
        { id: "followup", icon: PhoneCall, label: "Follow-up Sessions" },
    ];

    const profileMenuItems = [
        { id: "personalInfo", label: "Personal Info", icon: Info, component: <PersonalInfo /> },
        { id: "medicalHistory", label: "Medical History", icon: Activity, component: <MedicalHistory /> },
        { id: "lifestyle", label: "Lifestyle", icon: Heart, component: <Lifestyle /> },
        { id: "dietaryPreferences", label: "Dietary Preferences", icon: Apple, component: <DietaryPreferences /> },
        { id: "healthGoals", label: "Health Goals", icon: Target, component: <HealthGoals /> },
    ];

    const billingItems = [
        { id: "purchases", icon: CreditCard, label: "Purchases & Plans", component: <PurchasesPlans /> },
        { id: "paymentHistory", icon: Receipt, label: "Payment History", component: <PaymentHistory /> },
        { id: "support", icon: CircleHelp, label: "Support / Enquiry", component: <SupportEnquiry /> },
    ];

    useEffect(() => {
        const user = getUserData();
        if (user) {
            setUserData(user);
        }
    }, []);

    
    const handleEditProfile = () => {
        setActiveComponent("personalInfo");
    };

    const getInitials = (name) => {
        if (!name) return "U";
        return name.charAt(0).toUpperCase();
    };

    const fileInputRef = useRef(null);

    const handleAttachmentClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            console.log("Selected file:", file.name);
            alert(`File "${file.name}" selected successfully!`);
        }
    };

    const handleNavigation = (id) => {
        setActiveComponent(id);
        setIsMobileMenuOpen(false);
    };

    const handleProfileSubItemClick = (id) => {
        setActiveComponent(id);
        setIsMobileMenuOpen(false);
    };

    const toggleProfileDropdown = (e) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
        setIsProfileOpen(!isProfileOpen);
    };

    const renderActiveComponent = () => {
        switch (activeComponent) {
            case "dashboard":
                return (
                    <>
                        <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800 mb-2 font-lato">
                            Dashboard Overview
                        </h2>
                        <p className="text-gray-400 text-sm mb-8 font-lato">
                            Welcome back, Sarah. Here's a summary of your health journey.
                        </p>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6 mb-6 font-lato">
                            <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
                                <div className="flex items-center gap-2 mb-5">
                                    <User size={15} className="text-green-500" />
                                    <h3 className="text-[15px] font-semibold text-[#24312d]">
                                        Profile Summary
                                    </h3>
                                </div>

                                <div className="text-center mb-5">
                                    {userData?.profile_img ? (
                                        <img
                                            src={userData.profile_img}
                                            alt="profile"
                                            className="w-[72px] h-[72px] rounded-full mx-auto object-cover mb-3"
                                        />
                                    ) : (
                                        <div className="w-[72px] h-[72px] rounded-full mx-auto bg-[#57b26a] flex items-center justify-center mb-3">
                                            <span className="text-white text-2xl font-semibold">
                                                {getInitials(userData?.name)}
                                            </span>
                                        </div>
                                    )}
                                    <h4 className="text-[18px] font-semibold text-[#24312d] leading-none">
                                        {userData?.name || "Guest User"}
                                    </h4>
                                    <p className="text-[13px] text-[#98a19b] mt-2">
                                        {userData?.age || "—"} Years Old • {userData?.gender || "—"}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div className="bg-[#f7f9f7] rounded-lg p-3">
                                        <p className="text-[11px] text-[#9aa39d] mb-1">Current BMI</p>
                                        <h5 className="text-[15px] font-semibold text-[#24312d]">
                                            21.5 <span className="font-normal">(Normal)</span>
                                        </h5>
                                    </div>
                                    <div className="bg-[#f7f9f7] rounded-lg p-3">
                                        <p className="text-[11px] text-[#9aa39d] mb-1">Diet Type</p>
                                        <h5 className="text-[15px] font-semibold text-[#24312d]">
                                            Plant-based
                                        </h5>
                                    </div>
                                </div>

                                <div className="bg-[#f7f9f7] rounded-lg p-3 mb-4">
                                    <p className="text-[11px] text-[#9aa39d] mb-1">Primary Goal</p>
                                    <h5 className="text-[15px] font-semibold text-[#24312d]">
                                        Weight Maintenance & Energy
                                    </h5>
                                </div>

                                <button
                                    onClick={handleEditProfile}
                                    className="w-full h-[42px] bg-[#f7f9f7] border border-[#dfe5df] rounded-lg text-[14px] text-[#4d5954] hover:bg-gray-50 transition flex items-center justify-center gap-2"
                                >
                                    <Pencil size={16} />
                                    Edit Profile
                                </button>
                            </div>

                            <div className="xl:col-span-2 bg-white border border-[#e8ece8] rounded-xl p-5">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="flex items-center gap-2">
                                        <CalendarDays size={15} className="text-green-500" />
                                        <h3 className="text-[15px] font-semibold text-[#24312d]">
                                            Upcoming Appointments
                                        </h3>
                                    </div>
                                    <button className="text-[12px] text-[#57b26a] border border-gray-200 px-3 py-1 rounded-md hover:bg-gray-50 transition">
                                        View All
                                    </button>
                                </div>

                                <div className="border border-[#edf1ed] rounded-lg p-4 mb-4 bg-[#f7f9f7] ">
                                    <div className="flex items-center justify-between flex-wrap gap-4 ">
                                        <div className="flex items-center gap-4">
                                            <div className="w-[58px] h-[58px]  bg-white rounded-md flex flex-col items-center justify-center">
                                                <span className="text-[10px] text-[#57b26a] font-semibold">OCT</span>
                                                <span className="text-[22px] font-semibold text-[#24312d] leading-none">24</span>
                                            </div>
                                            <div>
                                                <h4 className="text-[15px] font-semibold text-[#24312d] mb-1">
                                                    Nutrition Follow-up Consultation
                                                </h4>
                                                <p className="text-[13px] text-[#97a09a] flex items-center gap-1">
                                                    <User size={12} />
                                                    Dr. Emily Chen &nbsp; • &nbsp;
                                                    <Clock size={12} />
                                                    10:00 AM - 10:45 AM
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-[12px] bg-[#f8c96c] font-bold text-[#4e3a06] px-3 py-2 rounded-full">
                                                Upcoming
                                            </span>
                                            <button className="bg-[#57b26a] hover:bg-[#57b26a] text-white text-[13px] px-4 h-[36px] rounded-md flex items-center justify-center gap-2">
                                                <Video size={16} />
                                                Join Call
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="border border-[#edf1ed] rounded-lg p-4 opacity-70 bg-[#f7f9f7]">
                                    <div className="flex items-center justify-between flex-wrap gap-4">
                                        <div className="flex items-center gap-4 ">
                                            <div className="w-[58px] h-[58px] rounded-md bg-[#f7f9f7] flex flex-col items-center justify-center">
                                                <span className="text-[10px] text-[#8e9892] font-semibold">OCT</span>
                                                <span className="text-[22px] font-semibold text-[#24312d] leading-none">12</span>
                                            </div>
                                            <div>
                                                <h4 className="text-[15px] font-medium text-[#7d8882] mb-1">
                                                    Initial Assessment
                                                </h4>
                                                <p className="text-[13px] text-[#97a09a] flex items-center gap-1">
                                                    <User size={12} />
                                                    Dr. Emily Chen &nbsp; • &nbsp;
                                                    <Clock size={12} className="inline" />
                                                    Completed
                                                </p>
                                            </div>
                                        </div>
                                        <span className="text-[13px] text-[#97a09a]">Completed</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white border border-[#e8ece8] rounded-xl p-5 mb-6 font-lato">
                            <div className="flex items-center justify-between mb-5">
                                <div className="flex items-center gap-2">
                                    <Utensils size={15} className="text-green-500" />
                                    <h3 className="text-[15px] font-semibold text-[#24312d]">Active Diet Plan</h3>
                                </div>
                                <span className="text-[11px] bg-[#57b26a] text-white px-3 py-1 rounded-full">Active</span>
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 ">
                                <div className="xl:col-span-1 border border-[#edf1ed]  bg-[#f7f9f7] rounded-lg p-4">
                                    <h4 className="text-lg sm:text-xl lg:text-[22px] font-semibold text-[#24312d] mb-2 leading-none">
                                        7-Day Detox & Glow
                                    </h4>
                                    <p className="text-[13px] text-[#98a19b] leading-6 mb-5">
                                        Focused on hydration, gut health, and vital nutrients.
                                    </p>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[13px] font-medium text-[#24312d]">Weekly Adherence</span>
                                        <span className="text-[13px] text-[#98a19b]">Day 4 of 7</span>
                                    </div>
                                    <div className="w-full h-[6px] bg-[#edf1ed] rounded-full mb-5 overflow-hidden">
                                        <div className="w-[65%] h-full bg-[#57b26a] rounded-full"></div>
                                    </div>
                                    <button className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-[14px] text-green-500 hover:bg-green-50 transition">
                                        Download Full PDF Guide
                                    </button>
                                </div>

                                <div className="xl:col-span-2">
                                    <h4 className="text-[14px] font-semibold text-[#24312d] mb-4">Today's Meals</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {[
                                            { title: "Breakfast", desc: "Green smoothie with spinach, chia seeds, and almond milk.", kcal: "320 kcal", icon: Coffee, color: "#57b26a" },
                                            { title: "Lunch", desc: "Quinoa bowl with roasted chickpeas and avocado.", kcal: "450 kcal", icon: Salad, color: "#57b26a" },
                                            { title: "Dinner", desc: "Grilled salmon with steamed asparagus and lemon.", kcal: "510 kcal", icon: UtensilsCrossed, color: "#57b26a" },
                                        ].map((meal, index) => (
                                            <div key={index} className="border border-[#edf1ed] rounded-lg p-4">
                                                <div className="w-9 h-9 rounded-full bg-[#f7f9f7] flex items-center justify-center text-green-500 text-lg mb-4">
                                                    <meal.icon size={18} />
                                                </div>
                                                <h5 className="text-lg sm:text-xl lg:text-[22px] font-semibold text-[#24312d] mb-2">{meal.title}</h5>
                                                <p className="text-[13px] text-[#98a19b] leading-6 mb-4">{meal.desc}</p>
                                                <span className="text-[14px] text-green-500 font-medium">{meal.kcal}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6 mb-6 font-lato">
                            <div className="xl:col-span-2 bg-white border border-[#e8ece8] rounded-xl p-5">
                                <div className="flex items-center gap-2 mb-5">
                                    <PhoneCall size={15} className="text-green-500" />
                                    <h3 className="text-[15px] font-semibold text-[#24312d]">Book Follow-Up Session</h3>
                                </div>
                                <div className="mb-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="w-7 h-7 rounded-full bg-[#57b26a] text-white text-[13px] flex items-center justify-center font-medium">1</span>
                                        <p className="text-[14px] font-medium text-[#24312d]">Select Date</p>
                                    </div>
                                    <div className="ml-10">
                                        <div className="h-[42px] border border-[#e1e6e1] rounded-lg px-4 flex items-center justify-between text-[13px] text-[#9aa39d]">
                                            <span>Thursday, October 31, 2024</span>
                                            <CalendarDays size={15} />
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="w-7 h-7 rounded-full bg-[#57b26a] text-white text-[13px] flex items-center justify-center font-medium">2</span>
                                        <p className="text-[14px] font-medium text-[#24312d]">Select Time</p>
                                    </div>
                                    <div className="ml-10 flex flex-wrap gap-3">
                                        {["09:00 AM", "10:30 AM", "01:00 PM", "03:30 PM"].map((time, index) => (
                                            <button key={index} className="px-5 h-[38px] rounded-lg text-[13px] border border-[#e1e6e1] text-[#a0a8a2] hover:border-green-400">
                                                {time}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-5">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="w-7 h-7 rounded-full bg-[#57b26a] text-white text-[13px] flex items-center justify-center font-medium">3</span>
                                        <p className="text-[14px] font-medium text-[#24312d]">Choose Mode</p>
                                    </div>
                                    <div className="ml-10 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <button className="relative h-[42px] border border-green-500 bg-[#f7f9f7] rounded-lg px-4 flex items-center gap-2 text-[14px] text-[#24312d]">
                                            <Video size={16} />
                                            <span>Video Call</span>
                                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>
                                        </button>
                                        <button className="h-[42px] border border-[#e1e6e1] rounded-lg px-4 flex items-center gap-2 text-[14px] text-[#a0a8a2]">
                                            <Phone size={16} />
                                            <span>Voice Call</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="ml-10">
                                    <button className="bg-[#57b26a] hover:bg-[#57b26a] text-white px-6 h-[40px] rounded-md text-[14px]">
                                        Confirm Booking
                                    </button>
                                </div>
                            </div>

                            <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
                                <div className="flex items-center gap-2 mb-5">
                                    <Receipt size={15} className="text-green-500" />
                                    <h3 className="text-[15px] font-semibold text-[#24312d]">Plan Purchase Details</h3>
                                </div>
                                <div className="border border-[#edf1ed] rounded-xl  bg-[#f7f9f7] p-5 text-center">
                                    <div className="w-14 h-14 rounded-full bg-white mx-auto flex items-center justify-center text-green-500 text-xl mb-4">    <Crown size={24} />
                                    </div>
                                    <h4 className="text-xl sm:text-2xl lg:text-[26px] font-semibold text-[#24312d] mb-3">Premium Wellness Pro</h4>
                                    <span className="inline-block text-[12px] bg-[#57b26a] text-white px-4 py-1 rounded-full mb-5">Active Subscription</span>
                                    <div className="border-t border-[#edf1ed] pt-4 text-left space-y-3 mb-5">
                                        <div className="flex justify-between text-[14px]">
                                            <span className="text-[#98a19b]">Plan Duration</span>
                                            <span className="text-[#24312d] font-medium">3 Months</span>
                                        </div>
                                        <div className="flex justify-between text-[14px]">
                                            <span className="text-[#98a19b]">Renewal Date</span>
                                            <span className="text-[#24312d] font-medium">Nov 12, 2024</span>
                                        </div>
                                    </div>
                                    <button className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-[14px] text-green-500 transition flex items-center justify-center gap-2">
                                        <span>Manage Subscription</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6 mb-6 font-lato">
                            <div className="xl:col-span-2 bg-white border border-[#e8ece8] rounded-xl p-5">
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-2">
                                        <Receipt size={15} className="text-green-500" />
                                        <h3 className="text-[15px] font-semibold text-[#24312d]">Payment History</h3>
                                    </div>
                                    <button className="text-[12px] text-green-500 border border-[#dfe5df] px-4 py-1.5 rounded-md hover:bg-green-50 transition">
                                        Download CSV
                                    </button>
                                </div>
                                <div className="overflow-x-auto">
                                    <div className="min-w-[650px]">
                                        <div className="grid grid-cols-4 text-[12px] text-[#98a19b] pb-3 border-b border-[#edf1ed] mb-2">
                                            <span>Transaction ID</span>
                                            <span>Date</span>
                                            <span>Amount</span>
                                            <span>Status</span>
                                        </div>
                                        {[
                                            { id: "#TRX-98214", date: "Aug 12, 2024", amount: "$149.00" },
                                            { id: "#TRX-87442", date: "May 12, 2024", amount: "$149.00" },
                                            { id: "#TRX-76221", date: "Feb 12, 2024", amount: "$149.00" },
                                        ].map((item, index) => (
                                            <div key={index} className="grid grid-cols-4 items-center py-4 border-b border-[#f4f6f4] last:border-0">
                                                <span className="text-[13px] text-[#b1b8b3]">{item.id}</span>
                                                <span className="text-[14px] text-[#24312d]">{item.date}</span>
                                                <span className="text-[14px] font-medium text-[#24312d]">{item.amount}</span>
                                                <span className="inline-flex w-fit text-[11px] bg-[#57b26a] text-white px-3 py-1 rounded-full">Successful</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white border border-[#e8ece8] rounded-xl p-5">
                                <div className="flex items-center gap-2 mb-5">
                                    <CircleHelp size={15} className="text-green-500" />
                                    <h3 className="text-[15px] font-semibold text-[#24312d]">Enquiry / Support</h3>
                                </div>

                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.jpg,.png,.txt"
                                />

                                <div className="mb-4">
                                    <label className="block text-[13px] font-medium text-[#24312d] mb-2">Subject</label>
                                    <select className="w-full h-[42px] border border-[#e1e6e1] rounded-lg px-4 text-[13px] text-[#98a19b] outline-none bg-white">
                                        <option>Select a topic...</option>
                                        <option>Billing Issue</option>
                                        <option>Diet Plan Help</option>
                                        <option>Technical Support</option>
                                    </select>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-[13px] font-medium text-[#24312d] mb-2">Message</label>
                                    <textarea
                                        rows="5"
                                        placeholder="How can we help you today?"
                                        className="w-full border border-[#e1e6e1] rounded-lg px-4 py-3 text-[13px] text-[#24312d] placeholder-[#b0b7b1] outline-none resize-none"
                                    />
                                </div>

                                <button
                                    onClick={handleAttachmentClick}
                                    className="w-full h-[42px] border border-[#dfe5df] rounded-lg text-[14px] text-[#4d5954] hover:bg-gray-50 transition mb-4 flex items-center justify-center gap-2"
                                >
                                    <Paperclip size={16} />
                                    Add Attachment
                                </button>

                                <button className="w-full h-[42px] bg-[#57b26a] hover:bg-green-600 text-white rounded-lg text-[14px] transition">
                                    Submit Enquiry
                                </button>
                            </div>
                        </div>
                    </>
                );
            case "appointments":
                return <MyAppointments />;
            case "dietplans":
                return <DietPlans />;
            case "followup":
                return <FollowupSessions />;
            case "personalInfo":
                return <PersonalInfo />;
            case "medicalHistory":
                return <MedicalHistory />;
            case "lifestyle":
                return <Lifestyle />;
            case "dietaryPreferences":
                return <DietaryPreferences />;
            case "healthGoals":
                return <HealthGoals />;
            case "purchases":
                return <PurchasesPlans />;
            case "paymentHistory":
                return <PaymentHistory />;
            case "support":
                return <SupportEnquiry />;
            default:
                return <DashboardOverview />;
        }
    };

    return (
        <div className="min-h-screen bg-[#f8faf8] font-sans font-lato">
            <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-lg border border-gray-100"
            >
                <Menu size={20} className="text-gray-600" />
            </button>
            <div className={`fixed inset-0 z-50 lg:hidden transition-transform duration-300 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)} />
                <div className="relative w-[280px] h-full bg-white shadow-xl flex flex-col">
                    <div className="px-5 pt-6 pb-4 border-b border-gray-100 flex justify-between items-center">
                        <Link to="/" className="flex items-center">
                            <img
                                src={require('../../../src/assets/images/newlogo.png')}
                                alt="Logo"
                                className="h-auto w-[110px]"
                            />
                        </Link>
                        <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-lg bg-gray-100">
                            <X size={18} className="text-gray-600" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 py-4">
                        <div className="mb-6">
                            <p className="text-xs text-gray-400 uppercase mb-3 tracking-wide">Menu</p>
                            <div className="space-y-1">
                                {menuItems.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <div key={index}>
                                            <button
                                                onClick={() => {
                                                    if (item.hasDropdown) {
                                                        toggleProfileDropdown();
                                                    } else {
                                                        handleNavigation(item.id);
                                                    }
                                                }}
                                                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeComponent === item.id && !item.hasDropdown
                                                    ? "bg-green-50 text-[#57b26a] "
                                                    : "text-gray-600 hover:bg-gray-50"
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon size={18} />
                                                    {item.label}
                                                </div>
                                                {item.hasDropdown && (
                                                    isProfileOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                                                )}
                                            </button>

                                            {item.hasDropdown && isProfileOpen && (
                                                <div className="ml-8 mt-1 space-y-1">
                                                    {profileMenuItems.map((profileItem, idx) => {
                                                        const Icon = profileItem.icon;
                                                        return (
                                                            <button
                                                                key={idx}
                                                                onClick={() => handleProfileSubItemClick(profileItem.id)}
                                                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${activeComponent === profileItem.id
                                                                    ? "bg-green-50 text-[#57b26a]"
                                                                    : "text-gray-600 hover:bg-gray-50"
                                                                    }`}
                                                            >
                                                                <Icon size={14} />
                                                                {profileItem.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <p className="text-xs text-gray-400 uppercase mb-3 tracking-wide">Billing & Support</p>
                            <div className="space-y-1">
                                {billingItems.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <button
                                            key={index}
                                            onClick={() => handleNavigation(item.id)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-bold transition ${activeComponent === item.id
                                                ? "bg-green-50 text-[#57b26a] "
                                                : "text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            <Icon size={18} />
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <aside className="hidden lg:block bg-[#f4f6f5] fixed left-0 top-0 h-full w-[270px] bg-white border-r border-gray-200 overflow-y-auto">
                <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                    <Link to="/" className="flex items-center">
                        <img
                            src={require('../../../src/assets/images/newlogo.png')}
                            alt="Logo"
                            className="h-auto w-[125px]"
                        />
                    </Link>
                </div>
                <div className="px-6 py-4">
                    <div className="mb-8">
                        <p className="text-xs text-gray-400 uppercase mb-4 tracking-wide">Menu</p>
                        <div className="space-y-2">
                            {menuItems.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <div key={index}>
                                        <button
                                            onClick={() => {
                                                if (item.hasDropdown) {
                                                    toggleProfileDropdown();
                                                } else {
                                                    handleNavigation(item.id);
                                                }
                                            }}
                                            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm font-medium transition ${activeComponent === item.id && !item.hasDropdown
                                                ? "bg-green-50 text-[#57b26a]"
                                                : "text-gray-600 hover:bg-gray-50"
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 font-bold">
                                                <Icon size={18} />
                                                {item.label}
                                            </div>
                                            {item.hasDropdown && (
                                                isProfileOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                                            )}
                                        </button>

                                        {item.hasDropdown && isProfileOpen && (
                                            <div className="ml-6 mt-2 space-y-1">
                                                {profileMenuItems.map((profileItem, idx) => {
                                                    const Icon = profileItem.icon;
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => handleProfileSubItemClick(profileItem.id)}
                                                            className={`w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition ${activeComponent === profileItem.id
                                                                ? "bg-green-50 text-[#57b26a]"
                                                                : "text-gray-600 hover:bg-gray-50 hover:text-[#57b26a]"
                                                                }`}
                                                        >
                                                            <Icon size={16} />
                                                            {profileItem.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <p className="text-xs text-gray-400 font-bold uppercase mb-4 tracking-wide">Billing & Support</p>
                        <div className="space-y-2">
                            {billingItems.map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <button
                                        key={index}
                                        onClick={() => handleNavigation(item.id)}
                                        className={`w-full font-bold flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition ${activeComponent === item.id
                                            ? "bg-green-50 text-[#57b26a]"
                                            : "text-gray-600 hover:bg-gray-50"
                                            }`}
                                    >
                                        <Icon size={18} />
                                        {item.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </aside>
            <div className="lg:ml-[270px] flex flex-col min-h-screen">
                <header className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="w-10 lg:hidden"></div>
                        <div className="relative flex-1 max-w-[200px] sm:max-w-[300px] lg:max-w-[420px]">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search..."
                                className="w-full pl-9 pr-3 h-9 lg:h-10 rounded-lg border border-gray-100 outline-none text-sm placeholder:text-xs lg:placeholder:text-sm focus:border-green-400 focus:ring-1 focus:ring-green-400"
                            />
                        </div>

                        <div className="flex items-center gap-2 cursor-pointer">
                            {userData?.profile_img ? (
                                <img
                                    src={userData.profile_img}
                                    alt="user"
                                    className="w-8 h-8 lg:w-9 lg:h-9 rounded-full object-cover"
                                />
                            ) : (
                                <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-[#57b26a] flex items-center justify-center">
                                    <span className="text-white text-sm font-medium">
                                        {getInitials(userData?.name)}
                                    </span>
                                </div>
                            )}
                            <div className="text-sm hidden sm:block">
                                <p className="font-medium text-gray-800 text-sm">{userData?.name || "Guest User"}</p>
                                <p className="text-xs text-gray-400">{userData?.role === 3 ? "Premium Member" : "Free Member"}</p>
                            </div>
                        </div>
                    </div>
                </header>
                <main className="flex-1 p-4 sm:p-6 lg:p-8">
                    {renderActiveComponent()}
                </main>
            </div>
        </div>
    );
};

export default DashboardOverview;